import classnames from 'classnames';
import React from 'react';

import Button from 'dpl/common/design_system/Button';

interface SimilarBreedsSectionFooterProps {
  onViewAllClick: () => void;
  morePuppiesCount?: number | null;
  radiusInMiles?: number | null;
  className?: string | null;
}

/**
 * "View all" footer for both similar breeds variants. The puppy carousel passes
 * no counts and the button sits alone on the left; the breeder list passes them,
 * and the resulting helper text pushes the button to the right. `justify-between`
 * covers both, so neither layout needs its own branch.
 *
 * A button rather than a link: the destination is an overlay on this same page,
 * opened through a hash param.
 */
export default function SimilarBreedsSectionFooter({
  onViewAllClick,
  morePuppiesCount = null,
  radiusInMiles = null,
  className = null
}: SimilarBreedsSectionFooterProps) {
  // Both halves are required, not just the count: the radius arrives with the
  // results, and a sentence promising puppies within `undefined` miles is worse
  // than no sentence.
  const helperText =
    morePuppiesCount && radiusInMiles
      ? `See +${morePuppiesCount} more ${
          morePuppiesCount === 1 ? 'puppy' : 'puppies'
        } within ${radiusInMiles} miles`
      : null;

  return (
    <div
      className={classnames(
        'SimilarBreedsSectionFooter flex items-center justify-between gap-16',
        className
      )}
    >
      {helperText && <p className="font-16 secondary lh-body">{helperText}</p>}
      <Button
        className="nowrap font-16"
        data-test-id="similar_breeds_view_all_button"
        onClick={onViewAllClick}
        // The helper text shares the row, so the button tightens to leave it
        // more room.
        size={helperText ? 'xsmall' : 'small'}
        variant="primary"
      >
        View all
      </Button>
    </div>
  );
}
